//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import { Box, Paper, Tab } from '@mui/material'
import { PoolLifeCycleState, PoolTabEnum, PoolTypeEnum, RoleEnum } from '@shared/Enums'
import { ETH_ADDR_ZERO, EthAddr } from '@shared/SchemaTypes'
import { PoolDynamicOverviewCTO, PoolFlexOverviewCTO, PoolOverviewCTO } from '@shared/sharedTypes'
import React, { ReactElement, useEffect, useState } from 'react'

import { BorrowerWithdrawalTab } from '@react/borrower/BorrowerWithdrawalsTab'
import LoanList from '@react/components/loans/LoanList'
import PoolContractAddresses from '@react/components/pools/PoolContractAddresses'
import { PoolFlexCalendar } from '@react/components/pools/PoolFlexCalendat'
import PoolLegalPage from '@react/components/pools/PoolLegalPage'
import PoolSummary from '@react/components/pools/PoolSummary'
import PoolOverview from '@react/lender/PoolOverview'
import { useOtChainState, useOtSelector } from '@react/redux/store'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import AdminGrantPoolAccessPage from './AdminGrantPoolAccessPage'
import { AdminLenderView } from './AdminLenderView'
import AdminPoolActivationChecklist from './AdminPoolActivationChecklist'
import { AdminWithdrawalFlexTab } from './AdminWithdrawalsFlexTab'
import CollateralView from './CollateralView'
import { PoolOffchainDataView } from './PoolOffchainDataView'

import BorrowerAdminPoolActionModals from '@react/borrower/BorrowerAdminPoolActionModals'
import { AdminDynamicWithdrawalsTab } from '@react/dynamic/AdminDynamicWithdrawalsTab'
import DynamicBorrowerAdminPoolActionModals from '@react/dynamic/DynamicBorrowerAdminPoolActionModals'
import { DynamicDepositsTab } from '@react/dynamic/DynamicDepositsTab'
import { fetchDefiExchangeRate } from '@react/fetchers/defiFetchers'
import { DefiExchangeRateCTO } from '@shared/defiCTO'
import { logOT } from '@shared/loggingShared'
import { displayName } from '@shared/PoolBaseCTO'
import DefiExchangeRateCard from './DefiExchangeRateCard'
import DefiFeePage from './DefiFeePage'
import RewardsForPoolView from './RewardsForPoolView'
import DefiExchangeRatePage from './DefiExchangeRatePage'
import TicketPendingCard from './TicketPendingCard'

export default function AdminPoolDetails(props: { poolAddr?: EthAddr }): ReactElement {
  const p = useParams().poolAddr
  const poolAddr = (props.poolAddr ? props.poolAddr : p) as EthAddr
  const [searchParams, setSearchParams] = useSearchParams()

  const tab = searchParams.get('tab')

  const navigate = useNavigate()
  const [defiExchangeRateCTO, setDefiExchangeRateCTO] = useState<DefiExchangeRateCTO>()

  const {
    stageConfiguration: { serverApiBaseUrl, restAPIKey },
    userConnection: { roleEnum },
  } = useOtChainState()

  const poolBaseOverviewCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO])

  const allPools = useOtSelector((state) => state.poolState.poolOverviewCTOs)
  const poolOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.Term ? (poolBaseOverviewCTO as PoolOverviewCTO) : null
  const poolFlexOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.FlexibleRate ? (poolBaseOverviewCTO as PoolFlexOverviewCTO) : null
  const poolDynamicOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.Dynamic ? (poolBaseOverviewCTO as PoolDynamicOverviewCTO) : null
  const [selectedTab, setSelectedTab] = React.useState<PoolTabEnum>(tab ? (tab as PoolTabEnum) : PoolTabEnum.OVERVIEW)

  const [isLoadingExchangeRate, setIsLoadingExchangeRate] = useState(false)
  const refreshCallback = () => {
    if (poolAddr && poolDynamicOverviewCTO?.collateralSetCTO?.isDefi) {
      setIsLoadingExchangeRate(true)
      fetchDefiExchangeRate(serverApiBaseUrl, restAPIKey, poolAddr)
        .then((_defiExchangeRateCTO) => {
          setDefiExchangeRateCTO(_defiExchangeRateCTO)
          setIsLoadingExchangeRate(false)
        })
        .catch((error) => {
          setIsLoadingExchangeRate(false)
          logOT.error('Error fetching defi exchange rate check cto:', error)
        })
    }
  }

  useEffect(() => {
    refreshCallback()
  }, [poolAddr, serverApiBaseUrl, restAPIKey, poolDynamicOverviewCTO])
  const handleChange = (_event: React.SyntheticEvent, newValue: PoolTabEnum): void => {
    setSelectedTab(newValue)
    setSearchParams({ tab: newValue })
  }

  useEffect(() => {
    if (poolAddr && !poolBaseOverviewCTO && Object.keys(allPools).length > 0) {
      logOT.info('location navigate to /')
      navigate('/')
    }
  }, [poolAddr, poolBaseOverviewCTO, allPools])

  // Handle tab parameter from URL
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam === 'withdrawals') {
      setSelectedTab(PoolTabEnum.WITHDRAWS)
    } else if (tabParam === 'deposits') {
      setSelectedTab(PoolTabEnum.DEPOSITS)
    } else if (tabParam) {
      setSelectedTab(tabParam as PoolTabEnum)
    }
  }, [searchParams])

  if (
    roleEnum === RoleEnum.POOL_ADMIN &&
    (poolOverviewCTO?.state === PoolLifeCycleState.Initialized ||
      poolFlexOverviewCTO?.state === PoolLifeCycleState.Initialized)
  ) {
    return <AdminPoolActivationChecklist poolAddr={poolAddr} />
  }

  if (poolBaseOverviewCTO) {
    return (
      <Box data-testid="AdminPoolDetails">
        {(isLoadingExchangeRate || defiExchangeRateCTO) && (
          <DefiExchangeRateCard
            poolAddr={poolAddr}
            defiExchangeRateCTO={defiExchangeRateCTO}
            inFullPage={false}
            refreshCallback={refreshCallback}
          />
        )}
        <TicketPendingCard poolAddr={poolAddr} />
        {poolDynamicOverviewCTO && (
          <DynamicBorrowerAdminPoolActionModals poolAddr={poolAddr} poolName={displayName(poolBaseOverviewCTO)} />
        )}
        {!poolDynamicOverviewCTO && (
          <BorrowerAdminPoolActionModals poolName={displayName(poolBaseOverviewCTO)} poolAddr={poolAddr} />
        )}

        <PoolSummary poolAddr={poolAddr} />
        <Paper sx={{ marginTop: 4, marginBottom: 4 }}>
          <Box px={3} py={2}>
            <TabContext value={selectedTab}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList
                  onChange={handleChange}
                  aria-label="lab API tabs example"
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label={'Overview'} value={PoolTabEnum.OVERVIEW} />
                  {/* <Tab label={'Schedule'} value={PoolTabEnum.SCHEDULE} /> */}
                  {poolOverviewCTO && <Tab label={'Loans'} value={PoolTabEnum.LOANS} />}
                  {poolDynamicOverviewCTO && <Tab label={'Deposits'} value={PoolTabEnum.DEPOSITS} />}
                  <Tab label={'Withdrawals'} value={PoolTabEnum.WITHDRAWS} />
                  {(roleEnum === RoleEnum.POOL_ADMIN || roleEnum === RoleEnum.BORROWER) && (
                    <Tab label={'Lender View'} value={PoolTabEnum.LENDER_VIEW} />
                  )}
                  {poolDynamicOverviewCTO &&
                    poolDynamicOverviewCTO.collateralSetCTO?.isDefi &&
                    roleEnum === RoleEnum.POOL_ADMIN && <Tab label={'Fees'} value={PoolTabEnum.FEES} />}
                  {poolDynamicOverviewCTO &&
                    poolDynamicOverviewCTO.collateralSetCTO?.isDefi &&
                    roleEnum === RoleEnum.POOL_ADMIN && (
                      <Tab label={'Exchange Rate'} value={PoolTabEnum.DEFI_EXCHANGE_RATE} />
                    )}
                  {(roleEnum === RoleEnum.POOL_ADMIN || roleEnum === RoleEnum.BORROWER) && (
                    <Tab label={'Rewards'} value={PoolTabEnum.REWARDS} />
                  )}
                  <Tab label={'OffChainData'} value={PoolTabEnum.OFFCHAIN_DATA} />
                  <Tab label={'Access List'} value={PoolTabEnum.ACCESS_LIST} />
                  <Tab label={'Collateral'} value={PoolTabEnum.ASSETS} />
                  <Tab label={'Info'} value={PoolTabEnum.LEGAL} />
                  <Tab label={'Contract Addresses'} value={PoolTabEnum.CONTRACT_ADDRESSES} />
                  {poolBaseOverviewCTO?.poolType === PoolTypeEnum.FlexibleRate && (
                    <Tab label={'Calendar'} value={PoolTabEnum.FLEX_CALENDAR} />
                  )}
                </TabList>
              </Box>
              <TabPanel value={PoolTabEnum.OVERVIEW}>
                <PoolOverview />
              </TabPanel>
              {/* <TabPanel value={PoolTabEnum.SCHEDULE}><PoolSchedule/></TabPanel> */}
              {poolOverviewCTO && (
                <TabPanel value={PoolTabEnum.LOANS}>
                  <LoanList />
                </TabPanel>
              )}
              {poolDynamicOverviewCTO && (
                <TabPanel value={PoolTabEnum.DEPOSITS}>
                  <DynamicDepositsTab poolAddr={poolAddr} />
                </TabPanel>
              )}
              {poolOverviewCTO && (
                <TabPanel value={PoolTabEnum.WITHDRAWS}>
                  <BorrowerWithdrawalTab poolAddr={poolAddr}></BorrowerWithdrawalTab>
                </TabPanel>
              )}

              {poolFlexOverviewCTO && (
                <TabPanel value={PoolTabEnum.WITHDRAWS}>
                  <AdminWithdrawalFlexTab poolAddr={poolAddr} />
                </TabPanel>
              )}
              {poolDynamicOverviewCTO && (
                <TabPanel value={PoolTabEnum.WITHDRAWS}>
                  <AdminDynamicWithdrawalsTab poolAddr={poolAddr} />
                </TabPanel>
              )}
              {(roleEnum === RoleEnum.POOL_ADMIN || roleEnum === RoleEnum.BORROWER) && (
                <TabPanel value={PoolTabEnum.LENDER_VIEW}>
                  <AdminLenderView poolAddr={poolAddr} />
                </TabPanel>
              )}

              <TabPanel value={PoolTabEnum.REWARDS}>
                <RewardsForPoolView poolAddr={poolAddr} />
              </TabPanel>

              <TabPanel value={PoolTabEnum.ACCESS_LIST}>
                <AdminGrantPoolAccessPage poolAddr={poolAddr} />
              </TabPanel>
              <TabPanel value={PoolTabEnum.LEGAL}>
                <PoolLegalPage />
              </TabPanel>
              <TabPanel value={PoolTabEnum.OFFCHAIN_DATA}>
                <PoolOffchainDataView poolAddr={poolAddr} />
              </TabPanel>

              <TabPanel value={PoolTabEnum.ASSETS}>
                <CollateralView poolAddr={poolAddr} />
              </TabPanel>
              <TabPanel value={PoolTabEnum.CONTRACT_ADDRESSES}>
                <PoolContractAddresses role={RoleEnum.POOL_ADMIN} />
              </TabPanel>
              <TabPanel value={PoolTabEnum.FLEX_CALENDAR}>
                <PoolFlexCalendar poolAddr={poolAddr} role={RoleEnum.POOL_ADMIN} />
              </TabPanel>
              <TabPanel value={PoolTabEnum.FEES}>
                <DefiFeePage poolAddr={poolAddr} />
              </TabPanel>
              <TabPanel value={PoolTabEnum.DEFI_EXCHANGE_RATE}>
                <DefiExchangeRatePage poolAddr={poolAddr} />
              </TabPanel>
            </TabContext>
          </Box>
        </Paper>
      </Box>
    )
  } else {
    return <></>
  }
}
